import { AfterViewInit, Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { SharedModule } from '../../shared/shared.module'
import { HeaderComponent } from './header/header.component'
import { MainComponent } from './main/main.component'
import { firstValueFrom, Subject } from 'rxjs'
import {
	IPlugin,
	IPublicView,
	IPublicViewConfiguration,
	ModelService,
  IModuleWidget,
	PluginInjectorApplicationPlugin,
  PluginInjectorService
} from 'core-client/api'
import { ActivatedRoute, Params } from '@angular/router';

@Component({
    selector: 'app-dashboard',
    imports: [SharedModule, HeaderComponent, MainComponent],
    templateUrl: './dashboard.component.html',
    styleUrl: './dashboard.component.scss'
})
export class PublicDashboardComponent implements OnInit, AfterViewInit, OnDestroy {
  private ngUnsubscribe$: Subject<void> = new Subject<void>()
  isMobileView: boolean = false;
  publicViewUuid: string = '';
  private publicViewConfigurationSubject = new Subject<IPublicViewConfiguration>();
  publicViewConfiguration = this.publicViewConfigurationSubject.asObservable();

  @HostListener('window:resize', ['$event'])
  onResize(): void {
    this.checkIfMobileView();
  }

  constructor(private activatedRoute: ActivatedRoute, private modelService: ModelService, private pluginInjectorService: PluginInjectorService) { }

  async ngOnInit(): Promise<void> {
    this.checkIfMobileView();
  }

  async ngAfterViewInit(): Promise<void> {
    this.pluginInjectorService.widgetService.resetModuleWidgets()
    await this.loadRegisteredPlugin()
  }

  ngOnDestroy(): void {
    window.removeEventListener('resize', this.onResize)
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }

  // methods
  private checkIfMobileView(): void {
    this.isMobileView = window.innerWidth <= 768;
  }

  private async loadRegisteredPlugin(): Promise<void> {
    const routeParams: Params = await firstValueFrom(this.activatedRoute.params);
    this.publicViewUuid = routeParams['uuid'] ?? '';
    // find enabled public view configuration with given UUID
    const publicViews = await this.modelService._publicView.listAll(`enabled=true&uuid=${this.publicViewUuid}`);
    if (publicViews[0] != null) {
      const publicView: IPublicView = publicViews[0];
      if (publicView.source_type != null) {
        const sourceType = publicView.source_type.split('.');
        // number of substrings must be 3, because source_type is expected to have format '<pluginName>.<moduleName>.<widgetType>'
        if (sourceType.length === 3) {
          const plugin = this.pluginInjectorService.getPluginByName(sourceType[0].toUpperCase());
          if (plugin != null) {
            this.addDynamicWidgetComponents(plugin)
            if (plugin.enabled) {
              this.notifyObservingPlugins(plugin)
            }
          }
          this.publicViewConfigurationSubject.next({
            // as we currently only support one widget at a time in a public view, there is just one source type and one settings object provided
            widgets: [{
              pluginName: sourceType[0],
              moduleName: sourceType[1],
              widgetType: sourceType[2],
              settings: publicView.settings ?? {}
            }]
          });
        }
      }
    }
  }

  private addDynamicWidgetComponents(registeredPlugin: PluginInjectorApplicationPlugin.IApplicationPlugin): void {
    registeredPlugin.modules?.forEach((pluginModule: PluginInjectorApplicationPlugin.IPluginModule) => {
      if (pluginModule.widgets) {
        pluginModule.widgets.forEach((modulWidget: IModuleWidget) => {
          this.pluginInjectorService.widgetService.addModuleWidget(modulWidget)
        })
      }
    })
  }

  private notifyObservingPlugins(plugin: IPlugin): void {
    this.modelService._plugin.notifyPlugin(plugin, 'activate')
  }
}
